/* eslint-disable react-hooks/exhaustive-deps */
import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Divider } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import FilePicker from "components/FilePicker";
import { uploadImageV2 } from "services/uploadService";
import ColorPicker from "components/ColorPicker";
import {
  createCardBG,
  getCardBGInfo,
  updateCardBG,
} from "services/cardBGServices";

const _cardDetails = {
  name: "",
  textColor: "#282E13",
};

const CardBackgroundEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cardDetails, setCardDetails] = useState(_cardDetails);
  const [imageData, setImageData] = useState({
    cardImg: null,
    cardImgSm: null,
    detImg: null,
  });

  const resolutionMap = {
    cardImg: { width: 260, height: 400 },
    cardImgSm: { width: 260, height: 347 },
    detImg: { width: 1280, height: 581 },
  };

  const cancel = () =>
    navigate(configData.routes.cardBackground.cardBackground);

  const getPickerLabel = (type: any) => {
    switch (type) {
      case "cardImg":
        return "Card Image";
      case "cardImgSm":
        return "Card Small Image";
      case "detImg":
        return "Details BG";
      default:
        return "";
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    try {
      setLoading(true);
      let response;
      // await cardBGValidation.validate(cardDetails);

      if (id === "create") {
        response = await createCardBG(cardDetails);
      } else {
        const { _id, name, textColor } = cardDetails as any;

        let payload: any = { _id, name, textColor };

        for (const type of ["cardImg", "cardImgSm", "detImg"]) {
          if (cardDetails[`${type}FileType`])
            payload[`${type}FileType`] = cardDetails[`${type}FileType`];
        }

        response = await updateCardBG(payload);
      }


      if (response?.data) {
        const uploads = Object.keys(imageData).map((key) => {
          let url = response.data[`${key}UploadUrl`];
          if (url && imageData[key]) return upload(url, key);
        });

        await Promise.all(uploads);
      }

      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.cardBackground.cardBackground);
    } catch (error) {
      if (error?.inner)
        error.inner.forEach((error) => showSnackbar(error.message, "error"));
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    setCardDetails((state) => ({ ...state, [name]: value }));
  };

  const upload = async (url, key) => {
    try {
      const resp = await uploadImageV2({
        data: imageData[key],
        uploadUrl: url,
        resize: true,
        resizeToWidth: resolutionMap[key].width,
        resizeToHeight: resolutionMap[key].height,
      });

      if (resp.status === 200)
        showSnackbar(resp.message ?? "Image Uploaded", "success");
      return resp;
    } catch (error) {
      showSnackbar(error.message || "Error uploading image!");
    }
  };

  const onSelectImage = (image, type) => {
    setImageData((state) => ({ ...state, [type]: image }));

    setCardDetails((state) => ({
      ...state,
      [`${type}FileType`]: "image/webp",
    }));
  };

  const fetchCardBGInfo = async () => {
    try {
      setLoading(true);
      const resp = await getCardBGInfo(id);
      if (resp?.data) {
        setCardDetails({ ...resp.data });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    if (id !== "create") fetchCardBGInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Card BG`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Name"
              name="name"
              value={cardDetails.name}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <ColorPicker
              label={"Text Color"}
              name="textColor"
              value={cardDetails.textColor}
              onChange={onChange}
              customStyle={{ width: "100%", marginTop: 20 }}
            />
          </Box>
        </Box>
        {Object.keys(imageData).map((imageType) => {
          const label = getPickerLabel(imageType);
          return (
            <Box key={imageType} className={styles.imagePickerContainer}>
              <Box className={styles.imagePickerAndPreviewContainer}>
                <FilePicker
                  label={label}
                  value={imageData[imageType]}
                  name={imageType}
                  onFileSelect={(image) => onSelectImage(image, imageType)}
                />
                {cardDetails[imageType] && !imageData[imageType] && (
                  <Box
                    component="img"
                    src={cardDetails[imageType]}
                    alt={`${imageType} Image Preview`}
                    style={{
                      aspectRatio: "16/9",
                      maxHeight: 200,
                      maxWidth: 400,
                      objectFit: "contain",
                      marginTop: 2,
                    }}
                  />
                )}
              </Box>

              <Divider style={{ marginTop: 10 }} variant="fullWidth" />
            </Box>
          );
        })}
      </Box>
    </ContentShell>
  );
};

export default CardBackgroundEditor;
